import React, { useState } from 'react';
import { Server, Monitor, Loader2, ArrowRight } from 'lucide-react';

export function NetworkSetupModal({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState<'SELECT_ROLE' | 'INPUT_IP'>('SELECT_ROLE');
  const [role, setRole] = useState<'MAIN' | 'SECONDARY' | null>(null);
  const [serverIp, setServerIp] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (finalRole: 'MAIN' | 'SECONDARY', finalIp: string = '') => {
    setIsSaving(true);
    try {
      if ((window as any).electronAPI && (window as any).electronAPI.saveSettings) {
        await (window as any).electronAPI.saveSettings({
          role: finalRole,
          serverIp: finalIp,
        });
      }
      onComplete();
    } catch (err) {
      console.error('Error saving settings', err);
      setIsSaving(false);
    }
  };

  const handleRoleSelect = (selectedRole: 'MAIN' | 'SECONDARY') => {
    setRole(selectedRole);
    if (selectedRole === 'MAIN') {
      handleSave('MAIN');
    } else {
      setStep('INPUT_IP');
    }
  };

  const handleIpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (serverIp.trim()) {
      handleSave('SECONDARY', serverIp.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="bg-blue-600 p-8 text-center text-white">
          <h1 className="text-3xl font-bold mb-3">Configuración de Red</h1>
          <p className="text-blue-100 text-lg">
            ¿Cómo funcionará esta computadora en tu sucursal?
          </p>
        </div>

        <div className="p-8">
          {step === 'SELECT_ROLE' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={() => handleRoleSelect('MAIN')}
                disabled={isSaving}
                className="flex flex-col items-center p-8 border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group disabled:opacity-50"
              >
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Server className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Servidor Principal</h3>
                <p className="text-gray-500 text-center text-sm">
                  Esta es la computadora del gerente o la caja principal. Aquí se guardará la base de datos local de la sucursal.
                </p>
              </button>

              <button
                onClick={() => handleRoleSelect('SECONDARY')}
                disabled={isSaving}
                className="flex flex-col items-center p-8 border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group disabled:opacity-50"
              >
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Monitor className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Caja Secundaria</h3>
                <p className="text-gray-500 text-center text-sm">
                  Esta es una caja de cobro adicional. Se conectará por red local al Servidor Principal para guardar las ventas.
                </p>
              </button>
            </div>
          )}

          {step === 'INPUT_IP' && (
            <form onSubmit={handleIpSubmit} className="max-w-md mx-auto space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Monitor className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Conectar al Servidor</h3>
                <p className="text-gray-500 text-sm">
                  Ingresa la dirección IP de la computadora "Servidor Principal" en tu red local.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección IP del Servidor
                </label>
                <input
                  type="text"
                  value={serverIp}
                  onChange={(e) => setServerIp(e.target.value)}
                  placeholder="Ej: 192.168.1.100"
                  className="w-full text-center text-2xl tracking-widest font-mono p-4 border-2 border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  required
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep('SELECT_ROLE')}
                  className="w-1/3 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Volver
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !serverIp}
                  className="flex-1 flex justify-center items-center py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-all"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Conectar <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {isSaving && step === 'SELECT_ROLE' && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
              <p className="text-lg font-bold text-gray-900">Configurando sistema...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
